import { useToast } from "@/components/ui/toast"
import { getShopId } from "@/utils/functions/getShopId"
import { getToken } from "@/utils/functions/getToken"
import type {
  CashRegisterAddBeautyInput,
  CashRegisterAddBeautyOutput,
  CashRegisterGetBeautyOutput,
  CashRegisterHeartbeatBeautyInput,
  CashRegisterHeartbeatBeautyOutput,
  CashRegisterLockBeautyInput,
  CashRegisterLockBeautyOutput,
  CashRegisterTable,
  CashRegisterUnlockBeautyInput,
  CashRegisterUnlockBeautyOutput,
  CashRegisterUpdateNameBeautyInput,
  CashRegisterUpdateNameBeautyOutput,
  CashRegisterUpdateReaderIdBeautyInput,
  CashRegisterUpdateReaderIdBeautyOutput,
  CashRegisterUpdateStatusBeautyInput,
  CashRegisterUpdateStatusBeautyOutput,
} from "@okeys/shared-utils"
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from "@tanstack/react-query"
import { fetchUserAttributes } from "aws-amplify/auth"
import { customAxios } from "../utils/customAxios/customAxios"

// Hook pour récupérer les caisses enregistreuses d'un shop
export const useCashRegistersQuery = () => {
  const queryOptions: UseQueryOptions<
    CashRegisterGetBeautyOutput,
    Error,
    CashRegisterGetBeautyOutput,
    string[]
  > = {
    queryKey: ["cashRegisters"],
    queryFn: async () => {
      const token = await getToken()
      const userAttributes = await fetchUserAttributes()
      const shopId = userAttributes["custom:shopId"]

      if (!token) {
        throw new Error("No token")
      }

      if (!shopId) {
        throw new Error("Shop ID not found in user attributes")
      }

      const response = await customAxios.get(
        `/cash-register?shopId=${shopId}`,
        {
          headers: {
            Authorization: token,
          },
        }
      )

      return response.data as CashRegisterGetBeautyOutput
    },
    staleTime: 300000, // 5 minutes
    refetchOnWindowFocus: false,
  }

  const { isPending, isError, data, isSuccess, refetch, error } =
    useQuery(queryOptions)

  return {
    pendingCashRegisters: isPending,
    errorCashRegisters: isError,
    successCashRegisters: isSuccess,
    refetchCashRegisters: refetch,
    cashRegisters: data?.cashRegisters || [],
    cashRegistersCount: data?.count || 0,
    error,
  }
}

// Hook pour ajouter une nouvelle caisse enregistreuse
export const useAddCashRegisterMutation = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      cashRegisterData: CashRegisterAddBeautyInput
    ): Promise<CashRegisterAddBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.post(
        "/cash-register",
        cashRegisterData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      )

      return response.data as CashRegisterAddBeautyOutput
    },
    // Optimistic update : ajout immédiat dans l'UI
    onMutate: async (newCashRegister: CashRegisterAddBeautyInput) => {
      // Annuler les requêtes en cours pour éviter les conflits
      await queryClient.cancelQueries({ queryKey: ["cashRegisters"] })
      const shopId = await getShopId()

      // Sauvegarder l'état précédent pour le rollback
      const previousCashRegisters =
        queryClient.getQueryData<CashRegisterGetBeautyOutput>(["cashRegisters"])

      // Créer une caisse temporaire avec un ID fictif
      const tempCashRegister: CashRegisterTable = {
        caisseId: `temp-${Date.now()}`, // ID temporaire
        shopId: shopId || "",
        readerId: newCashRegister.readerId || "",
        status: newCashRegister.status || "inactive",
        dateCreation: new Date().toISOString(),
        name:
          newCashRegister.name || `Caisse ${Date.now().toString().slice(-4)}`,
        currentUserId: null,
        sessionStartTime: null,
        lastHeartbeat: null,
        isLocked: null,
        sessionId: null,
      }

      // Mettre à jour optimistiquement le cache
      queryClient.setQueryData<CashRegisterGetBeautyOutput>(
        ["cashRegisters"],
        (old) => {
          const currentCashRegisters = old?.cashRegisters || []
          return {
            cashRegisters: [...currentCashRegisters, tempCashRegister],
            count: (old?.count || 0) + 1,
          }
        }
      )

      // Retourner le contexte pour le rollback
      return { previousCashRegisters }
    },
    onSuccess: () => {
      // Invalider et refetch pour avoir les vraies données du serveur
      queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
    },
    onError: (
      error: Error,
      _variables: CashRegisterAddBeautyInput,
      context
    ) => {
      console.error("Erreur lors de l'ajout de la caisse:", error)
      // Rollback : restaurer l'état précédent
      if (context?.previousCashRegisters) {
        queryClient.setQueryData(
          ["cashRegisters"],
          context.previousCashRegisters
        )
      }

      addToast({
        type: "error",
        title: "Erreur lors de l'ajout",
        description: "Impossible d'ajouter la caisse enregistreuse",
        action: {
          label: "Réessayer",
          onClick: () => {
            // La mutation sera relancée depuis le composant
          },
        },
      })
    },
    onSettled: () => {
      // Toujours invalider les queries à la fin (succès ou erreur)
      queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
    },
  })
}

// Hook pour mettre à jour le nom d'une caisse
export const useUpdateCashRegisterNameMutation = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      updateData: CashRegisterUpdateNameBeautyInput
    ): Promise<CashRegisterUpdateNameBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.put(
        "/cash-register/name",
        updateData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      )

      return response.data as CashRegisterUpdateNameBeautyOutput
    },
    // Optimistic update : mise à jour immédiate du nom
    onMutate: async (updateData: CashRegisterUpdateNameBeautyInput) => {
      await queryClient.cancelQueries({ queryKey: ["cashRegisters"] })

      const previousCashRegisters =
        queryClient.getQueryData<CashRegisterGetBeautyOutput>(["cashRegisters"])

      // Mettre à jour optimistiquement le nom dans le cache
      queryClient.setQueryData<CashRegisterGetBeautyOutput>(
        ["cashRegisters"],
        (old) => {
          if (!old) return old

          const updatedCashRegisters = old.cashRegisters.map((cashRegister) =>
            cashRegister.caisseId === updateData.caisseId
              ? {
                  ...cashRegister,
                  name: updateData.name,
                  lastUpdated: new Date().toISOString(),
                }
              : cashRegister
          )

          return {
            ...old,
            cashRegisters: updatedCashRegisters,
          }
        }
      )

      return { previousCashRegisters }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
    },
    onError: (error: Error, _variables, context) => {
      console.error("Erreur lors de la mise à jour du nom:", error)
      if (context?.previousCashRegisters) {
        queryClient.setQueryData(
          ["cashRegisters"],
          context.previousCashRegisters
        )
      }

      addToast({
        type: "error",
        title: "Erreur de mise à jour",
        description: "Impossible de mettre à jour le nom de la caisse",
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
    },
  })
}

// Hook pour mettre à jour le readerId d'une caisse
export const useUpdateCashRegisterReaderIdMutation = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      updateData: CashRegisterUpdateReaderIdBeautyInput
    ): Promise<CashRegisterUpdateReaderIdBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.put(
        "/cash-register/reader-id",
        updateData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      )

      return response.data as CashRegisterUpdateReaderIdBeautyOutput
    },
    // Optimistic update : mise à jour immédiate du readerId
    onMutate: async (updateData: CashRegisterUpdateReaderIdBeautyInput) => {
      await queryClient.cancelQueries({ queryKey: ["cashRegisters"] })

      const previousCashRegisters =
        queryClient.getQueryData<CashRegisterGetBeautyOutput>(["cashRegisters"])

      // Mettre à jour optimistiquement le readerId dans le cache
      queryClient.setQueryData<CashRegisterGetBeautyOutput>(
        ["cashRegisters"],
        (old) => {
          if (!old) return old

          const updatedCashRegisters = old.cashRegisters.map((cashRegister) =>
            cashRegister.caisseId === updateData.caisseId
              ? {
                  ...cashRegister,
                  readerId: updateData.readerId,
                  lastUpdated: new Date().toISOString(),
                }
              : cashRegister
          )

          return {
            ...old,
            cashRegisters: updatedCashRegisters,
          }
        }
      )

      return { previousCashRegisters }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
    },
    onError: (error: Error, _variables, context) => {
      console.error("Erreur lors de la mise à jour du readerId:", error)
      if (context?.previousCashRegisters) {
        queryClient.setQueryData(
          ["cashRegisters"],
          context.previousCashRegisters
        )
      }

      addToast({
        type: "error",
        title: "Erreur d'association",
        description: "Impossible d'associer le terminal Stripe à la caisse",
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
    },
  })
}

// Hook pour mettre à jour le statut d'une caisse
export const useUpdateCashRegisterStatusMutation = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      updateData: CashRegisterUpdateStatusBeautyInput
    ): Promise<CashRegisterUpdateStatusBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.put(
        "/cash-register/status",
        updateData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      )

      return response.data as CashRegisterUpdateStatusBeautyOutput
    },
    // Optimistic update : mise à jour immédiate du statut
    onMutate: async (updateData: CashRegisterUpdateStatusBeautyInput) => {
      await queryClient.cancelQueries({ queryKey: ["cashRegisters"] })

      const previousCashRegisters =
        queryClient.getQueryData<CashRegisterGetBeautyOutput>(["cashRegisters"])

      // Mettre à jour optimistiquement le statut dans le cache
      queryClient.setQueryData<CashRegisterGetBeautyOutput>(
        ["cashRegisters"],
        (old) => {
          if (!old) return old

          const updatedCashRegisters = old.cashRegisters.map((cashRegister) =>
            cashRegister.caisseId === updateData.caisseId
              ? {
                  ...cashRegister,
                  status: updateData.status,
                  lastUpdated: new Date().toISOString(),
                }
              : cashRegister
          )

          return {
            ...old,
            cashRegisters: updatedCashRegisters,
          }
        }
      )

      return { previousCashRegisters }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
    },
    onError: (error: Error, _variables, context) => {
      console.error("Erreur lors de la mise à jour du statut:", error)
      if (context?.previousCashRegisters) {
        queryClient.setQueryData(
          ["cashRegisters"],
          context.previousCashRegisters
        )
      }

      addToast({
        type: "error",
        title: "Erreur de statut",
        description: "Impossible de mettre à jour le statut de la caisse",
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
    },
  })
}

// Hook pour verrouiller une caisse enregistreuse
export const useLockCashRegisterMutation = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      lockData: CashRegisterLockBeautyInput
    ): Promise<CashRegisterLockBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.post("/cash-register/lock", lockData, {
        headers: {
          Authorization: token,
          "Content-Type": "application/json",
        },
      })

      return response.data as CashRegisterLockBeautyOutput
    },
    onSuccess: (data) => {
      if (data.success) {
        // Invalider pour récupérer les vraies données du serveur
        queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
      }
    },
    onError: (error: Error) => {
      console.error("Erreur lors du verrouillage de la caisse:", error)

      addToast({
        type: "error",
        title: "Erreur de connexion",
        description: "Impossible de se connecter à la caisse",
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
    },
  })
}

// Hook pour déverrouiller une caisse enregistreuse
export const useUnlockCashRegisterMutation = () => {
  const queryClient = useQueryClient()
  const { addToast } = useToast()

  return useMutation({
    mutationFn: async (
      unlockData: CashRegisterUnlockBeautyInput
    ): Promise<CashRegisterUnlockBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.post(
        "/cash-register/unlock",
        unlockData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      )

      return response.data as CashRegisterUnlockBeautyOutput
    },
    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
      }
    },
    onError: (error: Error) => {
      console.error("Erreur lors du déverrouillage de la caisse:", error)

      addToast({
        type: "error",
        title: "Erreur de déconnexion",
        description: "Impossible de se déconnecter de la caisse",
      })
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["cashRegisters"] })
    },
  })
}

// Hook pour maintenir le heartbeat d'une caisse
export const useHeartbeatCashRegisterMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (
      heartbeatData: CashRegisterHeartbeatBeautyInput
    ): Promise<CashRegisterHeartbeatBeautyOutput> => {
      const token = await getToken()
      if (!token) {
        throw new Error("No token")
      }

      const response = await customAxios.post(
        "/cash-register/heartbeat",
        heartbeatData,
        {
          headers: {
            Authorization: token,
            "Content-Type": "application/json",
          },
        }
      )

      return response.data as CashRegisterHeartbeatBeautyOutput
    },
    // Pas d'optimistic update pour le heartbeat, c'est silencieux
    onSuccess: (data, variables) => {
      if (data.success) {
        // Mettre à jour silencieusement le timestamp du heartbeat
        queryClient.setQueryData<CashRegisterGetBeautyOutput>(
          ["cashRegisters"],
          (old) => {
            if (!old) return old

            const updatedCashRegisters = old.cashRegisters.map((cashRegister) =>
              cashRegister.caisseId === variables.caisseId
                ? {
                    ...cashRegister,
                    lastHeartbeat: new Date().toISOString(),
                  }
                : cashRegister
            )

            return {
              ...old,
              cashRegisters: updatedCashRegisters,
            }
          }
        )
      }
    },
    onError: (error: Error) => {
      // Heartbeat en erreur = session probablement expirée
      console.warn("Heartbeat failed:", error)
      // On pourrait ici déclencher une déconnexion automatique
      // ou afficher un avertissement discret
    },
    // Pas de toast ni d'invalidation pour le heartbeat
  })
}
